import { c as _c } from "react/compiler-runtime";
import { useApi } from '@netpurpose/api';
import { useAlertBanner, useInterval } from '@netpurpose/core';
import { useQueryClient } from '@tanstack/react-query';
import React, { type FC, useEffect, useReducer } from 'react';
import { type ITaskContext, reducer, type State, TaskType } from '#context/tasks';
import { config } from '#services/config';
import { checkPendingTaskResults } from './checkPendingTaskResults';
import { updateBanners } from './updateBanners';
const defaultState: State = {
  portfolioCreation: {},
  portfolioExport: {},
  portfolioPAIsExport: {},
  portfolioSDGsExport: {},
  portfolioSDGRevenueExport: {},
  entityExport: {},
  dataDictionaryExport: {}
};
export const defaultContext: ITaskContext = {
  createPortfolio: () => {},
  requestPortfolioExport: () => {},
  requestPortfolioPAIsExport: () => {},
  requestPortfolioSDGsExport: () => {},
  requestPortfolioSDGRevenueExport: () => {},
  requestEntityExport: () => {},
  requestDataDictionaryExport: () => {},
  removeExport: () => {},
  portfolioCreationTasks: [],
  portfolioExportRequests: [],
  portfolioPAIsExportRequests: [],
  portfolioSDGsExportRequests: [],
  portfolioSDGRevenueExportRequests: [],
  entityExportRequests: [],
  dataDictionaryExportRequests: []
};
export const TaskContext = React.createContext(defaultContext);
export const TaskProvider: FC<{
  children: React.ReactNode;
}> = props => {
  const $ = _c(43);
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(reducer, defaultState);
  const {
    api
  } = useApi();
  const {
    showAlertBanner,
    hideAlertBanner
  } = useAlertBanner();
  let t0;
  if ($[0] !== api.result || $[1] !== queryClient || $[2] !== showAlertBanner || $[3] !== state) {
    t0 = () => {
      checkPendingTaskResults(api.result, state, dispatch, queryClient, showAlertBanner);
    };
    $[0] = api.result;
    $[1] = queryClient;
    $[2] = showAlertBanner;
    $[3] = state;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  useInterval(t0, config.taskResultsPollDelay);
  let t1;
  let t2;
  if ($[5] !== hideAlertBanner || $[6] !== showAlertBanner || $[7] !== state) {
    t1 = () => updateBanners(state, showAlertBanner, hideAlertBanner);
    t2 = [hideAlertBanner, showAlertBanner, state];
    $[5] = hideAlertBanner;
    $[6] = showAlertBanner;
    $[7] = state;
    $[8] = t1;
    $[9] = t2;
  } else {
    t1 = $[8];
    t2 = $[9];
  }
  useEffect(t1, t2);
  let t3;
  if ($[10] !== state.portfolioCreation) {
    t3 = Object.values(state.portfolioCreation);
    $[10] = state.portfolioCreation;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const portfolioCreationTasks = t3;
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (token, portfolio) => dispatch({
      actionType: "new",
      taskType: TaskType.PortfolioCreation,
      token,
      initial: portfolio
    });
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const createPortfolio = t4;
  let t5;
  if ($[13] !== state.portfolioExport) {
    t5 = Object.values(state.portfolioExport);
    $[13] = state.portfolioExport;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const portfolioExportRequests = t5;
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (token_0, portfolioId, portfolioName, metricConfigId) => {
      dispatch({
        actionType: "new",
        taskType: TaskType.PortfolioExport,
        token: token_0,
        initial: portfolioId,
        portfolioName,
        metricConfigId
      });
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const requestPortfolioExport = t6;
  let t7;
  if ($[16] !== state.portfolioPAIsExport) {
    t7 = Object.values(state.portfolioPAIsExport);
    $[16] = state.portfolioPAIsExport;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const portfolioPAIsExportRequests = t7;
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (token_1, portfolioId_0, portfolioName_0) => {
      dispatch({
        actionType: "new",
        taskType: TaskType.PortfolioPAIsExport,
        token: token_1,
        initial: portfolioId_0,
        portfolioName: portfolioName_0
      });
    };
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const requestPortfolioPAIsExport = t8;
  let t9;
  if ($[19] !== state.portfolioSDGsExport) {
    t9 = Object.values(state.portfolioSDGsExport);
    $[19] = state.portfolioSDGsExport;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const portfolioSDGsExportRequests = t9;
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = (token_2, portfolioId_1, portfolioName_1) => {
      dispatch({
        actionType: "new",
        taskType: TaskType.PortfolioSDGsExport,
        token: token_2,
        initial: portfolioId_1,
        portfolioName: portfolioName_1
      });
    };
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const requestPortfolioSDGsExport = t10;
  let t11;
  if ($[22] !== state.portfolioSDGRevenueExport) {
    t11 = Object.values(state.portfolioSDGRevenueExport);
    $[22] = state.portfolioSDGRevenueExport;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const portfolioSDGRevenueExportRequests = t11;
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = (token_3, portfolioId_2, portfolioName_2) => {
      dispatch({
        actionType: "new",
        taskType: TaskType.PortfolioSDGRevenueExport,
        token: token_3,
        initial: portfolioId_2,
        portfolioName: portfolioName_2
      });
    };
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const requestPortfolioSDGRevenueExport = t12;
  let t13;
  if ($[25] !== state.entityExport) {
    t13 = Object.values(state.entityExport);
    $[25] = state.entityExport;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const entityExportRequests = t13;
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = (token_4, entityId, entityName) => dispatch({
      actionType: "new",
      taskType: TaskType.EntityExport,
      token: token_4,
      initial: entityId,
      entityName
    });
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const requestEntityExport = t14;
  let t15;
  if ($[28] !== state.dataDictionaryExport) {
    t15 = Object.values(state.dataDictionaryExport);
    $[28] = state.dataDictionaryExport;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const dataDictionaryExportRequests = t15;
  let t16;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = token_5 => dispatch({
      actionType: "new",
      taskType: TaskType.DataDictionaryExport,
      token: token_5,
      initial: "data-dictionary-export"
    });
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const requestDataDictionaryExport = t16;
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = (taskType, result) => {
      dispatch({
        actionType: "remove",
        taskType,
        result
      });
    };
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const removeExport = t17;
  let t18;
  if ($[32] !== dataDictionaryExportRequests || $[33] !== entityExportRequests || $[34] !== portfolioCreationTasks || $[35] !== portfolioExportRequests || $[36] !== portfolioPAIsExportRequests || $[37] !== portfolioSDGRevenueExportRequests || $[38] !== portfolioSDGsExportRequests) {
    t18 = {
      createPortfolio,
      portfolioCreationTasks,
      requestPortfolioExport,
      requestPortfolioPAIsExport,
      requestPortfolioSDGsExport,
      requestPortfolioSDGRevenueExport,
      portfolioExportRequests,
      portfolioPAIsExportRequests,
      portfolioSDGsExportRequests,
      portfolioSDGRevenueExportRequests,
      requestEntityExport,
      entityExportRequests,
      requestDataDictionaryExport,
      dataDictionaryExportRequests,
      removeExport
    };
    $[32] = dataDictionaryExportRequests;
    $[33] = entityExportRequests;
    $[34] = portfolioCreationTasks;
    $[35] = portfolioExportRequests;
    $[36] = portfolioPAIsExportRequests;
    $[37] = portfolioSDGRevenueExportRequests;
    $[38] = portfolioSDGsExportRequests;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== props || $[41] !== t18) {
    t19 = <TaskContext.Provider value={t18} {...props} />;
    $[40] = props;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  return t19;
};
export const useTasks = () => {
  const context = React.useContext(TaskContext);
  if (context === defaultContext) {
    throw new Error("useTasks must be used within a TaskProvider");
  }
  return context;
};